import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';
import api from '@/lib/api';
import { useBusinessParameters } from '../hooks';
import { ArcheHeader } from '../../../components/ArcheHeader';

interface BusinessStatsData {
  totalBusinesses: number;
  activeBusinesses: number;
  dissolvedBusinesses: number;
  businessesByType: Record<string, number>;
  totalCollections: number;
  totalHeptalsEarned: number;
}

export function AdminBusinessesPage() {
  const { user } = useAuth();
  const isAdmin = useMemo(() => user?.roles?.includes('admin') ?? false, [user?.roles]);
  const [stats, setStats] = useState<BusinessStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const { parameters } = useBusinessParameters();

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    async function fetchStats() {
      try {
        const result = await api.getBusinessStats();
        setStats(result);
      } catch {
        // API will reject non-admins
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_businesses" title="Business Admin" />
        <div className="max-w-4xl mx-auto p-6 w-full">
          <div className="flex items-center gap-2 text-muted-foreground">
            <ShieldAlert className="h-5 w-5" />
            <p>You do not have permission to view this page.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_businesses" title="Business Admin" />
        <div className="max-w-4xl mx-auto p-6 space-y-4 w-full">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_businesses" title="Business Admin" />
      <div className="max-w-4xl mx-auto p-6 space-y-6 w-full">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total Businesses</p>
                <p className="text-2xl font-bold">{stats.totalBusinesses}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold">{stats.activeBusinesses}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total Collections</p>
                <p className="text-2xl font-bold">{stats.totalCollections.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total Heptals Earned</p>
                <p className="text-2xl font-bold">{stats.totalHeptalsEarned.toLocaleString()}</p>
              </CardContent>
            </Card>
            {Object.entries(stats.businessesByType).map(([type, count]) => (
              <Card key={type}>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground capitalize">{type}</p>
                  <p className="text-2xl font-bold">{count}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Tabs defaultValue="mining">
          <TabsList>
            <TabsTrigger value="mining">Mining</TabsTrigger>
            <TabsTrigger value="farming">Farming</TabsTrigger>
            <TabsTrigger value="equipment">Equipment</TabsTrigger>
            <TabsTrigger value="effician">Effician</TabsTrigger>
          </TabsList>
          {['mining', 'farming', 'equipment', 'effician'].map((type) => (
            <TabsContent key={type} value={type} className="space-y-2">
              {parameters
                .filter((p) => p.businessType === type)
                .map((p) => (
                  <Card key={p.paramId}>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Level {p.level}</CardTitle>
                    </CardHeader>
                    <CardContent className="py-2">
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <span className="text-muted-foreground">Heptal Reward: </span>
                          <span className="font-medium">{p.rewards.heptals}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">COGS: </span>
                          <span className="font-medium">{p.requirements.cogsValue}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Food: </span>
                          <span className="font-medium">{p.requirements.foodValue}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Jion: </span>
                          <span className="font-medium">{p.requirements.jion}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
